/* =============================================================================
   CORE / SESSION-IDLE — MainUser-Auto-Logout bei Inaktivität (S140)
   =============================================================================
   Deckelt die Supabase-Auth-Session (MainUser) zusätzlich zum geräte-gated
   Operator-Idle: nach X Minuten Inaktivität wird der MainUser ausgeloggt.

   WARUM persistent (localStorage, nicht nur In-Memory): der Client läuft mit
   persistSession=true → die Auth-Session überlebt Browser-/Gerät-Schließen.
   Ein reiner In-Memory-Timer würde beim Schließen verschwinden und den
   „Browser war stundenlang zu"-Fall NICHT abdecken. Deshalb: Last-Activity in
   localStorage stempeln + beim App-Boot prüfen.

   Aufbau: pure Entscheidungslogik (idleShouldLogout / bootCheckDecision /
   clampTimeoutMinutes — Vitest-getestet) + dünner Side-Effect-Layer
   (localStorage-Wrapper + Watch). Die Activity-Stempel hängen an den
   bestehenden pointerdown/keydown-Listenern in operator-ui.js (NICHT doppelt
   binden, Memory gg-module-extract / feedback_gg_no_view_hijack: Logout ist die
   erlaubte View-Hijack-Ausnahme).
============================================================================ */

const LAST_ACTIVITY_KEY = 'gg_main_last_activity';

export const DEFAULT_TIMEOUT_MIN = 180;
export const MIN_TIMEOUT_MIN = 15;   // Sicherheits-Clamp: kein echtes „Aus" für den MainUser
export const MAX_TIMEOUT_MIN = 720;
const IDLE_CHECK_MS = 30000;

/* -------------------------------------------------------------------------- */
/* Pure Logik (Vitest)                                                         */
/* -------------------------------------------------------------------------- */

/** Pure: Idle-Timeout überschritten? (Spiegel von operator-ui _idleShouldLock).
 *  timeoutMs <= 0 oder unbekannte Last-Activity → niemals auslösen. */
export function idleShouldLogout(nowMs, lastActivityMs, timeoutMs) {
  if (!(timeoutMs > 0)) return false;
  if (!Number.isFinite(lastActivityMs)) return false;
  return (nowMs - lastActivityMs) >= timeoutMs;
}

/** Pure: Boot-Entscheidung.
 *   - kein/ungültiger Timestamp (frischer Login) → 'stamp'
 *   - Timestamp älter als Timeout (Browser war zu / lange idle) → 'logout'
 *   - sonst → 'ok' */
export function bootCheckDecision(nowMs, lastActivityMs, timeoutMs) {
  if (!Number.isFinite(lastActivityMs)) return 'stamp';
  return idleShouldLogout(nowMs, lastActivityMs, timeoutMs) ? 'logout' : 'ok';
}

/** Pure: User-Minuten in den erlaubten Bereich clampen (Default 180, 15–720).
 *  Min-Clamp 15 garantiert timeoutMs > 0 → idleShouldLogout ist nie „aus". */
export function clampTimeoutMinutes(raw) {
  let n = parseInt(raw, 10);
  if (!Number.isFinite(n)) n = DEFAULT_TIMEOUT_MIN;
  return Math.max(MIN_TIMEOUT_MIN, Math.min(MAX_TIMEOUT_MIN, n));
}

/* -------------------------------------------------------------------------- */
/* localStorage-Wrapper (try/catch wie getDeviceId)                            */
/* -------------------------------------------------------------------------- */

/** Stempelt die letzte Aktivität (Default: jetzt). Throttling macht der Caller. */
export function markMainActivity(nowMs) {
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  try { localStorage.setItem(LAST_ACTIVITY_KEY, String(now)); } catch (_) { /* ignore */ }
  return now;
}

/** Liest die letzte Aktivität (ms) oder null. */
export function readLastActivity() {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch (_) { return null; }
}

/** Entfernt den Stempel (beim Logout — sonst würde der nächste frische Login
 *  per Boot-Check sofort wieder rausfliegen). */
export function clearLastActivity() {
  try { localStorage.removeItem(LAST_ACTIVITY_KEY); } catch (_) { /* ignore */ }
}

/* -------------------------------------------------------------------------- */
/* Watch (Side-Effects)                                                        */
/* -------------------------------------------------------------------------- */

let _interval = null;
let _opts = null;
let _triggering = false;

function _trigger() {
  if (_triggering) return;
  _triggering = true;
  stopMainIdleWatch();
  const cb = _opts && _opts.onLogout;
  Promise.resolve()
    .then(() => (typeof cb === 'function' ? cb() : undefined))
    .catch((e) => { try { console.warn('[session-idle] onLogout:', e && e.message); } catch (_) { /* ignore */ } })
    .finally(() => { _triggering = false; });
}

function _tick() {
  if (!_opts) return;
  const now = Date.now();
  const last = readLastActivity();
  const timeoutMs = _opts.getTimeoutMs();
  if (!Number.isFinite(last)) { markMainActivity(now); return; }
  if (idleShouldLogout(now, last, timeoutMs)) _trigger();
}

/** Startet den MainUser-Idle-Watcher. Idempotent (läuft schon → no-op).
 *  Führt EINEN Boot-Check aus:
 *    - 'logout' → sofort onLogout (deckt „Browser war zu" ab)
 *    - 'stamp'  → Last-Activity setzen (frischer Login)
 *    - 'ok'     → weiterlaufen lassen
 *  @param {{getTimeoutMs:()=>number, onLogout:()=>any}} opts */
export function startMainIdleWatch(opts) {
  if (!opts || typeof opts.getTimeoutMs !== 'function' || typeof opts.onLogout !== 'function') return;
  _opts = opts;
  if (_interval) return; // bereits aktiv — Boot-Check lief schon
  const decision = bootCheckDecision(Date.now(), readLastActivity(), _opts.getTimeoutMs());
  if (decision === 'logout') { _trigger(); return; }
  if (decision === 'stamp') markMainActivity();
  if (typeof setInterval === 'function') {
    _interval = setInterval(_tick, IDLE_CHECK_MS);
  }
}

/** Stoppt den Watcher (Logout-Pfad / Test-Cleanup). */
export function stopMainIdleWatch() {
  if (_interval) { clearInterval(_interval); _interval = null; }
}

/** Nur für Tests: Modul-State zurücksetzen. */
export function _resetForTest() {
  stopMainIdleWatch();
  _opts = null;
  _triggering = false;
}
